<template>
  <div class="gene">
    <AnalysisCardSelection
      v-if="!analysisType"
      module-name="Genotype"
      module-description="Browse genes, variants, annotations, and genomic evidence across the multi-omics landscape."
      base-path="/gene"
    />

    <template v-else>
      <div class="bg-decoration">
        <svg class="molecule-bg molecule-bg-1" viewBox="0 0 400 400" fill="none">
          <circle cx="200" cy="80" r="6" fill="#fca5a5" opacity="0.6"/>
          <circle cx="120" cy="140" r="5" fill="#fca5a5" opacity="0.5"/>
          <circle cx="280" cy="140" r="7" fill="#fca5a5" opacity="0.7"/>
          <circle cx="80" cy="220" r="4" fill="#fca5a5" opacity="0.4"/>
          <circle cx="200" cy="200" r="8" fill="#fca5a5" opacity="0.8"/>
          <circle cx="320" cy="220" r="5" fill="#fca5a5" opacity="0.5"/>
          <circle cx="150" cy="280" r="6" fill="#fca5a5" opacity="0.6"/>
          <circle cx="250" cy="280" r="5" fill="#fca5a5" opacity="0.5"/>
          <circle cx="200" cy="350" r="4" fill="#fca5a5" opacity="0.4"/>
          <line x1="200" y1="80" x2="120" y2="140" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
          <line x1="200" y1="80" x2="280" y2="140" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
          <line x1="120" y1="140" x2="80" y2="220" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
          <line x1="120" y1="140" x2="200" y2="200" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
          <line x1="280" y1="140" x2="200" y2="200" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
          <line x1="280" y1="140" x2="320" y2="220" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
          <line x1="200" y1="200" x2="150" y2="280" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
          <line x1="200" y1="200" x2="250" y2="280" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
          <line x1="150" y1="280" x2="200" y2="350" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
          <line x1="250" y1="280" x2="200" y2="350" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
          <line x1="80" y1="220" x2="150" y2="280" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
          <line x1="320" y1="220" x2="250" y2="280" stroke="#fca5a5" stroke-width="1" opacity="0.3"/>
        </svg>

        <svg class="dna-bg" viewBox="0 0 200 400" fill="none">
          <path d="M40 20 Q100 60 40 100 Q-20 140 40 180 Q100 220 40 260 Q-20 300 40 340 Q100 380 40 420"
                stroke="#f87171" stroke-width="2" fill="none" opacity="0.2"/>
          <path d="M160 20 Q100 60 160 100 Q220 140 160 180 Q100 220 160 260 Q220 300 160 340 Q100 380 160 420"
                stroke="#f87171" stroke-width="2" fill="none" opacity="0.2"/>
          <line x1="60" y1="60" x2="140" y2="60" stroke="#f87171" stroke-width="1" opacity="0.15"/>
          <line x1="50" y1="100" x2="150" y2="100" stroke="#f87171" stroke-width="1" opacity="0.15"/>
          <line x1="60" y1="140" x2="140" y2="140" stroke="#f87171" stroke-width="1" opacity="0.15"/>
          <line x1="50" y1="180" x2="150" y2="180" stroke="#f87171" stroke-width="1" opacity="0.15"/>
          <line x1="60" y1="220" x2="140" y2="220" stroke="#f87171" stroke-width="1" opacity="0.15"/>
          <line x1="50" y1="260" x2="150" y2="260" stroke="#f87171" stroke-width="1" opacity="0.15"/>
          <line x1="60" y1="300" x2="140" y2="300" stroke="#f87171" stroke-width="1" opacity="0.15"/>
          <line x1="50" y1="340" x2="150" y2="340" stroke="#f87171" stroke-width="1" opacity="0.15"/>
        </svg>

        <svg class="bars-bg" viewBox="0 0 200 300" fill="none">
          <rect x="10" y="150" width="12" height="150" fill="#fca5a5" opacity="0.15"/>
          <rect x="30" y="100" width="12" height="200" fill="#fca5a5" opacity="0.12"/>
          <rect x="50" y="120" width="12" height="180" fill="#fca5a5" opacity="0.18"/>
          <rect x="70" y="80" width="12" height="220" fill="#fca5a5" opacity="0.1"/>
          <rect x="90" y="140" width="12" height="160" fill="#fca5a5" opacity="0.15"/>
          <rect x="110" y="90" width="12" height="210" fill="#fca5a5" opacity="0.13"/>
          <rect x="130" y="110" width="12" height="190" fill="#fca5a5" opacity="0.16"/>
          <rect x="150" y="70" width="12" height="230" fill="#fca5a5" opacity="0.09"/>
          <rect x="170" y="130" width="12" height="170" fill="#fca5a5" opacity="0.14"/>
        </svg>
      </div>

      <div class="gene-content">
        <div class="header-section">
          <div class="breadcrumb">
            <button class="back-btn" @click="goBackToSelection">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              <span>Back to Gene</span>
            </button>
            <span class="breadcrumb-sep">/</span>
            <span class="breadcrumb-current">{{ analysisLabel }}</span>
          </div>
          <h1 class="page-title">Gene Database</h1>
          <p class="page-subtitle">
            {{ analysisType === 'disease' ? 'Gene–Disease Association Results' : analysisType === 'metabolites' ? 'Gene–Metabolite Association Results' : 'Gene Mediation Analysis Results' }}
          </p>
          <p class="page-description">
            {{ pageDescription }}
          </p>
        </div>

        <div class="filters-section" :style="metabolitesFiltersStyle">
          <div class="filter-group">
            <label class="filter-label">rs-ID</label>
            <el-select v-model="selectedRsId" placeholder="Select rs-ID" class="filter-select" size="small" filterable>
              <el-option
                v-for="g in rsIds"
                :key="g"
                :label="g"
                :value="g">
              </el-option>
            </el-select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Sex</label>
            <el-select v-model="selectedSex" class="filter-select" size="small">
              <el-option label="All" value="all"></el-option>
              <el-option label="Female" value="female"></el-option>
              <el-option label="Male" value="male"></el-option>
            </el-select>
          </div>
          <template v-if="analysisType === 'mediation'">
            <div class="filter-divider"></div>
            <div class="filter-group">
              <label class="filter-label">Target Disease Category</label>
              <el-select v-model="targetCategory" placeholder="Select target disease category" class="filter-select" size="small" :disabled="!isSourceSelected" filterable>
                <el-option
                  v-for="cat in diseaseCategories"
                  :key="cat"
                  :label="cat"
                  :value="cat">
                </el-option>
              </el-select>
            </div>
            <div class="filter-group">
              <label class="filter-label">Target Disease</label>
              <el-select v-model="targetDisease" placeholder="Select target disease" class="filter-select" size="small" filterable :filter-method="filterTargetMeasurements" :disabled="!isSourceSelected">
                <el-option
                  v-for="meas in filteredDiseases"
                  :key="meas.diseaseId"
                  :label="meas.diseaseName"
                  :value="meas.diseaseId">
                </el-option>
              </el-select>
            </div>
            <div class="filter-group">
              <label class="filter-label">Target Sex</label>
              <el-select v-model="targetSex" class="filter-select" size="small" :disabled="!isSourceSelected">
                <el-option label="All" value="all"></el-option>
                <el-option label="Female" value="female"></el-option>
                <el-option label="Male" value="male"></el-option>
              </el-select>
            </div>
          </template>
          <el-button type="danger" @click="handleBrowse" size="small" class="browse-btn">
            <i class="el-icon-search"></i>
            Browse
          </el-button>
        </div>

        <div class="main-content" :style="metabolitesMainContentStyle">
          <div class="results-panel">
            <div class="panel-header">
              <div class="panel-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                {{ panelTitle }}
              </div>
              <div class="panel-subtitle">Showing rs-ID variant-associated results.</div>
              <button class="download-btn" @click="handleDownload">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Download
              </button>
            </div>
            <div class="table-container" v-loading="tableLoading">
              <table class="results-table">
                <thead>
                  <tr>
                    <th
                      v-for="col in tableColumns"
                      :key="col.key"
                      class="sortable-header"
                      :class="{ active: sortKey === col.key }"
                      @click="handleSort(col.key)"
                    >
                      <span class="header-label">{{ col.label }}</span>
                      <span class="sort-icons">
                        <span class="sort-icon" :class="{ active: sortKey === col.key && sortOrder === 'asc' }">↓</span>
                        <span class="sort-icon" :class="{ active: sortKey === col.key && sortOrder === 'desc' }">↑</span>
                      </span>
                    </th>
                  </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, idx) in tableData" :key="idx">
                          <td v-for="col in tableColumns" :key="col.key">
                            <a
                              v-if="isTraitColumn(col) && row[col.key]"
                              href="#"
                              class="trait-link"
                              @click.prevent="openTraitSearch(row[col.key])"
                            >{{ row[col.key] }}</a>
                            <MetaboliteTooltip v-else-if="isMetaboliteColumn(col)" :value="row[col.key]" />
                            <span v-else>{{ row[col.key] }}</span>
                          </td>
                        </tr>
                  <tr v-if="tableData.length === 0 && !tableLoading">
                    <td :colspan="tableColumns.length" class="empty-cell">No data available. Please select filters and click Browse.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="table-footer">
              <div class="table-info">
                Showing {{ tableData.length > 0 ? (currentPage - 1) * pageSize + 1 : 0 }} to {{ Math.min(currentPage * pageSize, total) }} of {{ total }} results
              </div>
              <div class="pagination">
                <el-pagination
                  background
                  layout="prev, pager, next"
                  :current-page="currentPage"
                  :page-size="pageSize"
                  :total="total"
                  @current-change="handlePageChange"
                />
              </div>
            </div>
            <FieldGlossary
              v-if="tableGlossaryGroups.length"
              class="results-glossary"
              :title="tableGlossaryTitle"
              :groups="tableGlossaryGroups"
              :columns="tableGlossaryColumns"
            />
          </div>

          <div class="charts-panel">
            <div class="chart-card">
              <div class="panel-header">
                <div class="panel-title">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="20" x2="18" y2="10"></line>
                    <line x1="12" y1="20" x2="12" y2="4"></line>
                    <line x1="6" y1="20" x2="6" y2="14"></line>
                  </svg>
                  {{ chartTitle }}
                </div>
                <div class="panel-subtitle">{{ chartSubtitle }}</div>
              </div>
              <div class="chart-container" v-loading="imageLoading">
                <div v-if="topTraitImages.length > 0" class="images-grid">
                  <el-image
                    v-for="(img, idx) in topTraitImages"
                    :key="idx"
                    :src="img.src"
                    :alt="img.name || (chartTitle + ' ' + (idx + 1))"
                    class="top-trait-image"
                    :preview-src-list="topTraitImages.map(item => item.src)"
                    fit="contain"
                    @error="handleImageError"
                  />
                </div>
                <div v-else class="image-placeholder">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  <span>Select filters and click Browse to view chart</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="analysisType === 'metabolites'" class="mqtl-section">
          <div class="chart-card">
            <div class="panel-header">
              <div class="panel-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M8 12h8"></path>
                  <path d="M12 8v8"></path>
                </svg>
                metQTL Module
              </div>
              <div class="panel-subtitle">Browse metQTL labels, paged records, and related resources below the existing metabolite module.</div>
            </div>
            <div class="mqtl-module">
              <div class="mqtl-filters">
                <div class="filter-group">
                  <label class="filter-label">Metabolite Label</label>
                  <el-select v-model="selectedMqtlId" placeholder="Select metabolite label" class="filter-select" size="small" filterable>
                    <el-option
                      v-for="label in mqtlLabels"
                      :key="label.id"
                      :label="label.name"
                      :value="label.id">
                    </el-option>
                  </el-select>
                </div>
                <div class="filter-group">
                  <label class="filter-label">Sex</label>
                  <el-select v-model="selectedMqtlSex" class="filter-select" size="small">
                    <el-option label="All" value="all"></el-option>
                    <el-option label="Female" value="female"></el-option>
                    <el-option label="Male" value="male"></el-option>
                  </el-select>
                </div>
                <el-button type="danger" size="small" class="browse-btn" @click="handleMqtlBrowse">
                  <i class="el-icon-search"></i>
                  Browse
                </el-button>
              </div>

              <div class="mqtl-content">
                <div class="results-panel mqtl-results-panel">
                  <div class="panel-header">
                    <div class="panel-title">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                      metQTL Results
                    </div>
                    <div class="panel-subtitle">Showing metabolite label-associated metQTL results.</div>
                    <button class="download-btn" @click="handleMqtlDownload">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Download
                    </button>
                  </div>
                  <div class="table-container" v-loading="mqtlTableLoading">
                    <table class="results-table">
                      <thead>
                        <tr>
                          <th
                            v-for="col in mqtlTableColumns"
                            :key="col.key"
                            class="sortable-header"
                            :class="{ active: mqtlSortKey === col.key }"
                            @click="handleMqtlSort(col.key)"
                          >
                            <span class="header-label">{{ col.label }}</span>
                            <span class="sort-icons">
                              <span class="sort-icon" :class="{ active: mqtlSortKey === col.key && mqtlSortOrder === 'asc' }">↓</span>
                              <span class="sort-icon" :class="{ active: mqtlSortKey === col.key && mqtlSortOrder === 'desc' }">↑</span>
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, idx) in mqtlTableData" :key="`mqtl-row-${idx}`">
                          <td v-for="col in mqtlTableColumns" :key="col.key">
                            <a
                              v-if="isTraitColumn(col) && row[col.key]"
                              href="#"
                              class="trait-link"
                              @click.prevent="openTraitSearch(row[col.key])"
                            >{{ row[col.key] }}</a>
                            <MetaboliteTooltip v-else-if="isMetaboliteColumn(col)" :value="row[col.key]" />
                            <span v-else>{{ row[col.key] }}</span>
                          </td>
                        </tr>
                        <tr v-if="mqtlTableData.length === 0 && !mqtlTableLoading">
                          <td :colspan="mqtlTableColumns.length || 1" class="empty-cell">No metQTL data available. Please select metabolite label and sex, then click Browse.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div class="table-footer">
                    <div class="table-info">
                      Showing {{ mqtlTableData.length > 0 ? (mqtlCurrentPage - 1) * mqtlPageSize + 1 : 0 }} to {{ Math.min(mqtlCurrentPage * mqtlPageSize, mqtlTotal) }} of {{ mqtlTotal }} results
                    </div>
                    <div class="pagination">
                      <el-pagination
                        background
                        layout="prev, pager, next"
                        :current-page="mqtlCurrentPage"
                        :page-size="mqtlPageSize"
                        :total="mqtlTotal"
                        @current-change="handleMqtlPageChange"
                      />
                    </div>
                  </div>
                  <FieldGlossary
                    v-if="mqtlGlossaryGroups.length"
                    class="results-glossary"
                    :title="mqtlGlossaryTitle"
                    :groups="mqtlGlossaryGroups"
                    :columns="2"
                  />
                </div>

                <div class="chart-card mqtl-resource-panel">
                  <div class="panel-header">
                    <div class="panel-title">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      metQTL Resources
                    </div>
                    <div class="panel-subtitle">Images are shown inline. Other resources are provided as downloadable files.</div>
                  </div>
                  <div class="resource-container" v-loading="resourceLoading">
                    <template v-if="mqtlResources.length > 0">
                      <div v-if="imageResources.length > 0" class="resource-section">
                        <div class="resource-section-title">Images</div>
                        <div class="images-grid">
                          <el-image
                            v-for="(img, idx) in imageResources"
                            :key="`img-${idx}`"
                            :src="img.src"
                            :alt="img.name || (`Resource image ${idx + 1}`)"
                            class="top-trait-image"
                            :preview-src-list="imageResources.map(item => item.src)"
                            fit="contain"
                            @error="handleMqtlResourceError(idx, 'image')"
                          />
                        </div>
                      </div>
                      <div v-if="fileResources.length > 0" class="resource-section">
                        <div class="resource-section-title">Files</div>
                        <div class="resource-file-list">
                          <a
                            v-for="(file, idx) in fileResources"
                            :key="`file-${idx}`"
                            class="resource-file-item"
                            :href="file.src"
                            :download="file.name || true"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14 2 14 8 20 8"></polyline>
                              <line x1="12" y1="18" x2="12" y2="12"></line>
                              <polyline points="9 15 12 18 15 15"></polyline>
                            </svg>
                            <span class="resource-file-name">{{ file.name || `File ${idx + 1}` }}</span>
                          </a>
                        </div>
                      </div>
                    </template>
                    <div v-else class="image-placeholder">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <path d="M8 12h8"></path>
                        <path d="M12 8v8"></path>
                      </svg>
                      <span>Select metabolite label and sex, then click Browse to view resources</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import request, { downloadFile } from '@/utils/request'
import FieldGlossary from '@/components/FieldGlossary.vue'
import MetaboliteTooltip from '@/components/MetaboliteTooltip.vue'
import AnalysisCardSelection from './AnalysisCardSelection.vue'

export default {
  name: 'Gene',
  components: {
    AnalysisCardSelection,
    FieldGlossary,
    MetaboliteTooltip
  },
  data() {
    return {
      module: 'gene',
      selectedRsId: '',
      selectedSex: 'all',
      targetCategory: '',
      targetDisease: '',
      targetDiseaseKeyword: '',
      targetSex: 'all',
      selectedMqtlId: '',
      selectedMqtlSex: 'all',
      rsIds: [],
      mqtlLabels: [],
      diseaseCategories: [],
      diseases: [],
      tableColumns: [],
      tableData: [],
      tableLoading: false,
      sortKey: '',
      sortOrder: '',
      currentPage: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0,
      mqtlTableColumns: [],
      mqtlTableData: [],
      mqtlTableLoading: false,
      mqtlSortKey: '',
      mqtlSortOrder: '',
      mqtlCurrentPage: 1,
      mqtlPageSize: 10,
      mqtlTotal: 0,
      mqtlTotalPages: 0,
      topTraitImages: [],
      imageLoading: false,
      mqtlResources: [],
      resourceLoading: false
    }
  },
  computed: {
    analysisType() {
      return this.$route.params.analysisType || ''
    },
    analysisLabel() {
      const labels = {
        disease: 'Disease Analysis',
        metabolites: 'Metabolites Analysis',
        mediation: 'Mediation Analysis'
      }
      return labels[this.analysisType] || ''
    },
    panelTitle() {
      const titles = {
        disease: 'rs-ID–Disease Associations',
        metabolites: 'rs-ID–Metabolite Associations',
        mediation: 'rs-ID Mediation Effects'
      }
      return titles[this.analysisType] || 'rs-ID Association Results'
    },
    pageDescription() {
      const descriptions = {
        disease: 'Select rs-ID and sex group, then click Browse to retrieve genotype–disease association results. The left panel displays detailed variant–disease association statistics with downloadable results, while the right panel summarizes the top disease traits associated with the selected genetic variant, ranked by effect size for rapid interpretation.',
        metabolites: 'Select the metabolite label and sex group, then click Browse to retrieve metabolite quantitative trait locus (metQTL) results. The left panel displays detailed genotype–metabolite association statistics with downloadable records, while the right panel provides related metQTL resources and visualizations for exploring the genetic architecture underlying the selected metabolite.',
        mediation: 'Select the specific rs-ID, sex group, target disease category, target disease, and target sex, then click Browse to retrieve genotype–metabolite–disease mediation results. The left panel presents detailed mediation effect statistics with downloadable results, while the right panel highlights the top candidate metabolite mediators ranked by effect size for rapid pathway prioritization.'
      }
      return descriptions[this.analysisType] || ''
    },
    chartTitle() {
      if (this.analysisType === 'mediation') {
        return 'Top Mediation Traits'
      }
      return 'Top Variant Traits'
    },
    chartSubtitle() {
      return 'Top five traits ranked by effect size.'
    },
    tableGlossaryTitle() {
      return 'Disease Result Fields'
    },
    tableGlossaryColumns() {
      return 1
    },
    tableGlossaryGroups() {
      if (this.analysisType !== 'disease') {
        return []
      }
      return [{
        items: [
          { term: 'FieldID', description: 'Unique identifier for the disease endpoint.' },
          { term: 'Trait', description: 'Name of the disease or phenotype.' },
          { term: 'Category', description: 'Disease category of the corresponding trait.' },
          { term: 'Beta (β)', description: 'Estimated effect size; the sign indicates the direction of association.' },
          { term: 'CI', description: 'Confidence interval for the estimated effect.' },
          { term: 'Z', description: 'Z statistic for the association test.' },
          { term: 'P', description: 'P value for the association test.' }
        ]
      }]
    },
    mqtlGlossaryTitle() {
      return 'Field Notes'
    },
    mqtlGlossaryGroups() {
      if (this.analysisType !== 'metabolites') {
        return []
      }
      return [{
        items: [
          { term: 'chr', description: 'Chromosome.' },
          { term: 'rs', description: 'dbSNP reference SNP identifier.' },
          { term: 'ps', description: 'Genomic position in base pairs.' },
          { term: 'n_mis', description: 'Number of samples with missing genotype data.' },
          { term: 'n_obs', description: 'Number of samples included in the analysis.' },
          { term: 'allele1', description: 'Effect allele.' },
          { term: 'allele0', description: 'Other allele.' }
        ]
      }, {
        items: [
          { term: 'AF', description: 'Effect allele frequency.' },
          { term: 'Beta (β)', description: 'Estimated effect size.' },
          { term: 'SE', description: 'Standard error of the estimate.' },
          { term: 'P_Wald', description: 'P value from the Wald test.' },
          { term: 'PIP_SuSiE', description: 'Posterior inclusion probability estimated by SuSiE.' },
          { term: 'P_FWER', description: 'Family-wise error rate-adjusted P value.' },
          { term: 'P_Bonferroni', description: 'Bonferroni-adjusted P value.' }
        ]
      }]
    },
    bottomCardTitle() {
      if (this.analysisType === 'mediation') {
        return 'Top Five Mediation Traits'
      }
      return 'Top Five Variant Traits'
    },
    metabolitesMainContentStyle() {
      return this.analysisType === 'metabolites' ? { display: 'none' } : {}
    },
    metabolitesFiltersStyle() {
      return this.analysisType === 'metabolites' ? { display: 'none' } : {}
    },
    filteredDiseases() {
      if (!this.targetDiseaseKeyword) return this.diseases
      const keyword = this.targetDiseaseKeyword.toLowerCase()
      return this.diseases.filter(m =>
        (m.diseaseId && m.diseaseId.toLowerCase().includes(keyword)) ||
        (m.diseaseName && m.diseaseName.toLowerCase().includes(keyword))
      )
    },
    imageResources() {
      return this.mqtlResources.filter(item => item.kind === 'image')
    },
    fileResources() {
      return this.mqtlResources.filter(item => item.kind === 'file')
    },
    isSourceSelected() {
      return !!this.selectedRsId
    }
  },
  watch: {
    analysisType() {
      this.selectedRsId = ''
      this.selectedSex = 'all'
      this.targetCategory = ''
      this.targetDisease = ''
      this.targetDiseaseKeyword = ''
      this.targetSex = 'all'
      this.selectedMqtlId = ''
      this.selectedMqtlSex = 'all'
      this.tableColumns = []
      this.tableData = []
      this.total = 0
      this.totalPages = 0
      this.currentPage = 1
      this.sortKey = ''
      this.sortOrder = ''
      this.mqtlTableColumns = []
      this.mqtlTableData = []
      this.mqtlTotal = 0
      this.mqtlTotalPages = 0
      this.mqtlCurrentPage = 1
      this.mqtlSortKey = ''
      this.mqtlSortOrder = ''
      this.topTraitImages = []
      this.mqtlResources = []
      this.initializeAnalysisData()
    },
    selectedRsId(newVal) {
      this.targetCategory = ''
      this.targetDisease = ''
      this.diseases = []
      if (this.analysisType === 'mediation' && newVal) {
        this.loadDiseaseCategories()
      }
    },
    targetCategory(newVal) {
      if (newVal && this.isSourceSelected) {
        this.loadDiseases(newVal)
        this.targetDisease = ''
      } else {
        this.diseases = []
      }
    },
    targetSex() {
      if (this.targetCategory && this.isSourceSelected) {
        this.loadDiseases(this.targetCategory)
        this.targetDisease = ''
      }
    },
  },
  mounted() {
    this.initializeAnalysisData()
  },
  methods: {
    async initializeAnalysisData() {
      this.loadRsIds()

      if (this.analysisType === 'metabolites') {
        this.loadMqtlLabels()
      }

      if (this.analysisType === 'mediation') {
        this.loadDiseaseCategories()
      }
    },
    async loadRsIds() {
      try {
        const data = await request.get(`/api/${this.module}/rs-id`)
        this.rsIds = data || []
      } catch (error) {
        console.error('Failed to load rs IDs:', error)
      }
    },
    async loadMqtlLabels() {
      try {
        const data = await request.get('/api/mqtl/labels')
        this.mqtlLabels = Array.isArray(data) ? data : (Array.isArray(data.data) ? data.data : [])
      } catch (error) {
        console.error('Failed to load mQTL labels:', error)
        this.mqtlLabels = []
      }
    },
    async loadDiseaseCategories() {
      if (!this.selectedRsId) {
        this.diseaseCategories = []
        return
      }
      try {
        const data = await request.get('/api/gene/mediation/disease/categories', {
          params: { rsId: this.selectedRsId }
        })
        this.diseaseCategories = data || []
      } catch (error) {
        console.error('Failed to load disease categories:', error)
      }
    },
    async loadDiseases(category) {
      try {
        const data = await request.get('/api/gene/mediation/disease/list', {
          params: { rsId: this.selectedRsId, category, sex: this.targetSex }
        })
        this.diseases = data || []
        this.targetDiseaseKeyword = ''
      } catch (error) {
        console.error('Failed to load diseases:', error)
      }
    },
    filterTargetMeasurements(keyword) {
      this.targetDiseaseKeyword = keyword
    },
    goBackToSelection() {
      this.$router.push('/gene')
    },
    handleBrowse() {
      this.currentPage = 1
      this.loadTableData()
      this.loadTopTraitImage()
    },
    handleSort(columnKey) {
      if (this.sortKey !== columnKey) {
        this.sortKey = columnKey
        this.sortOrder = 'desc'
      } else if (this.sortOrder === 'desc') {
        this.sortOrder = 'asc'
      } else if (this.sortOrder === 'asc') {
        this.sortKey = ''
        this.sortOrder = ''
      } else {
        this.sortOrder = 'desc'
      }
      this.currentPage = 1
      this.loadTableData()
    },
    isTraitColumn(column) {
      const value = `${column.key || ''} ${column.label || ''}`.toLowerCase()
      return value.includes('trait')
    },
    isMetaboliteColumn(column) {
      const value = `${column.key || ''} ${column.label || ''}`.toLowerCase()
      return value.includes('metabolite')
    },
    openTraitSearch(trait) {
      const keyword = String(trait || '').trim()
      if (keyword) {
        this.$router.push({ path: '/global-search', query: { keyword } })
      }
    },
    handlePageChange(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return
      this.currentPage = page
      this.loadTableData()
    },
    async handleDownload() {
      if (!this.selectedRsId) {
        this.$message.warning('Please select an rs-ID.')
        return
      }
      if (this.analysisType === 'mediation' &&
          (!this.targetDisease || !this.targetCategory)) {
        this.$message.warning('Please select both Target Category and Target Measurement.')
        return
      }
      const params = {
        rsId: this.selectedRsId,
        sex: this.selectedSex
      }
      if (this.analysisType === 'mediation') {
        params.targetDisease = this.targetDisease
        params.targetSex = this.targetSex
      }
      await downloadFile(`/api/${this.module}/${this.analysisType}/download`, params, `gene_${this.analysisType}_${this.selectedRsId}.tsv`)
    },
    handleMqtlBrowse() {
      this.mqtlCurrentPage = 1
      this.loadMqtlTableData()
      this.loadMqtlResources()
    },
    handleMqtlSort(columnKey) {
      if (this.mqtlSortKey !== columnKey) {
        this.mqtlSortKey = columnKey
        this.mqtlSortOrder = 'desc'
      } else if (this.mqtlSortOrder === 'desc') {
        this.mqtlSortOrder = 'asc'
      } else if (this.mqtlSortOrder === 'asc') {
        this.mqtlSortKey = ''
        this.mqtlSortOrder = ''
      } else {
        this.mqtlSortOrder = 'desc'
      }
      this.mqtlCurrentPage = 1
      this.loadMqtlTableData()
    },
    async handleMqtlDownload() {
      if (!this.selectedMqtlId) {
        this.$message.warning('Please select a metabolite label.')
        return
      }
      await downloadFile('/api/mqtl/download', {
        mqtlId: this.selectedMqtlId,
        sex: this.selectedMqtlSex
      }, `mqtl_${this.selectedMqtlId}_${this.selectedMqtlSex}.tsv`)
    },
    handleMqtlPageChange(page) {
      if (page < 1 || page > this.mqtlTotalPages || page === this.mqtlCurrentPage) return
      this.mqtlCurrentPage = page
      this.loadMqtlTableData()
    },
    async loadTableData() {
      if (!this.selectedRsId) {
        this.$message.warning('Please select an rs-ID.')
        return
      }
      if (this.analysisType === 'mediation' &&
          (!this.targetDisease || !this.targetCategory)) {
        this.$message.warning('Please select both Target Category and Target Measurement.')
        return
      }
      this.tableLoading = true
      try {
        const params = {
          rsId: this.selectedRsId,
          sex: this.selectedSex,
          pageNum: this.currentPage,
          pageSize: this.pageSize
        }
        if (this.sortKey && this.sortOrder) {
          params.sortBy = this.sortKey
          params.sortOrder = this.sortOrder
        }
        if (this.analysisType === 'mediation') {
          params.targetDisease = this.targetDisease
          params.targetCategory = this.targetCategory
          params.targetSex = this.targetSex
        }
        const data = await request.get(`/api/${this.module}/${this.analysisType}/page`, { params })
        const headers = data.headers || []
        const records = data.records || []
        this.tableColumns = headers.map(h => ({ key: h, label: h }))
        this.tableData = records.map(row => {
          const obj = {}
          headers.forEach((h, i) => { obj[h] = row[i] })
          return obj
        })
        this.total = data.total || 0
        this.totalPages = data.pages || 0
      } catch (error) {
        console.error('Failed to load table data:', error)
        this.tableColumns = []
        this.tableData = []
        this.total = 0
        this.totalPages = 0
      } finally {
        this.tableLoading = false
      }
    },
    async loadMqtlTableData() {
      if (!this.selectedMqtlId) {
        this.$message.warning('Please select a metabolite label.')
        return
      }

      this.mqtlTableLoading = true
      try {
        const params = {
          mqtlId: this.selectedMqtlId,
          sex: this.selectedMqtlSex,
          pageNum: this.mqtlCurrentPage,
          pageSize: this.mqtlPageSize
        }
        if (this.mqtlSortKey && this.mqtlSortOrder) {
          params.sortBy = this.mqtlSortKey
          params.sortOrder = this.mqtlSortOrder
        }
        const response = await request.get('/api/mqtl/page', {
          params
        })
        const data = this.unwrapMqtlPayload(response)
        const headers = data.headers || []
        const records = data.records || []
        this.mqtlTableColumns = headers.map(h => ({ key: h, label: h }))
        this.mqtlTableData = records.map(row => {
          const obj = {}
          headers.forEach((h, i) => { obj[h] = row[i] })
          return obj
        })
        this.mqtlTotal = data.total || records.length || 0
        this.mqtlTotalPages = data.pages || Math.ceil(this.mqtlTotal / this.mqtlPageSize) || 0
      } catch (error) {
        console.error('Failed to load mQTL table data:', error)
        this.mqtlTableColumns = []
        this.mqtlTableData = []
        this.mqtlTotal = 0
        this.mqtlTotalPages = 0
      } finally {
        this.mqtlTableLoading = false
      }
    },
    async loadMqtlResources() {
      if (!this.selectedMqtlId) {
        this.mqtlResources = []
        return
      }

      this.resourceLoading = true
      this.mqtlResources = []
      try {
        const response = await request.get('/api/mqtl/resources', {
          params: {
            mqtlId: this.selectedMqtlId,
            sex: this.selectedMqtlSex
          }
        })
        const list = this.unwrapMqtlResourceList(response)
        this.mqtlResources = list.map(this.normalizeMqtlResource).filter(Boolean)
      } catch (error) {
        console.error('Failed to load mQTL resources:', error)
        this.mqtlResources = []
      } finally {
        this.resourceLoading = false
      }
    },
    async loadTopTraitImage() {
      if (!this.selectedRsId) {
        this.topTraitImages = []
        return
      }
      if (this.analysisType === 'mediation' &&
          (!this.targetDisease || !this.targetCategory)) {
        this.topTraitImages = []
        return
      }
      this.imageLoading = true
      this.topTraitImages = []
      try {
        const params = {
          rsId: this.selectedRsId,
          sex: this.selectedSex
        }
        if (this.analysisType === 'mediation') {
          params.targetDisease = this.targetDisease
          params.targetCategory = this.targetCategory
          params.targetSex = this.targetSex
        }
        const imageData = await request.get(`/api/${this.module}/${this.analysisType}/top-trait-image`, { params })
        if (imageData) {
          const rawList = this.normalizeImageList(imageData)
          this.topTraitImages = rawList.map(this.normalizeImageResource).filter(Boolean)
        }
      } catch (error) {
        console.error('Failed to load top trait image:', error)
        this.topTraitImages = []
      } finally {
        this.imageLoading = false
      }
    },
    normalizeImageList(imageData) {
      if (!imageData) return []
      if (Array.isArray(imageData)) return imageData
      if (Array.isArray(imageData.data)) return imageData.data
      return [imageData]
    },
    normalizeImageResource(item) {
      if (!item) return null

      if (typeof item === 'string') {
        return {
          name: '',
          type: 'image/png',
          src: item.startsWith('data:image') ? item : `data:image/png;base64,${item}`
        }
      }

      const type = this.normalizeImageType(item.type || item.contentType)
      const resource = item.resource
      const payload = item.base64 || item.data || item.content || (
        typeof resource === 'string'
          ? resource
          : resource && typeof resource === 'object'
            ? (resource.data || resource.base64 || resource.content || resource.url || '')
            : ''
      )

      if (!payload) return null

      const normalizedPayload = typeof payload === 'string' ? payload.trim().replace(/\s+/g, '') : payload

      const src = normalizedPayload.startsWith('data:image')
        ? normalizedPayload
        : this.buildImageSrc(type, normalizedPayload)

      if (!src) return null

      return {
        name: item.name || '',
        type,
        src
      }
    },
    normalizeImageType(type) {
      if (!type || typeof type !== 'string') return 'image/png'

      const normalizedType = type.trim().toLowerCase()
      if (normalizedType.startsWith('image/')) return normalizedType
      if (normalizedType === 'image') return 'image/png'
      if (normalizedType === 'file') return 'file'

      return 'image/png'
    },
    buildImageSrc(type, payload) {
      if (type === 'file') {
        if (/^(https?:)?\/\//.test(payload) || payload.startsWith('/')) {
          return payload
        }
        return null
      }

      return `data:${type};base64,${payload}`
    },
    unwrapMqtlPayload(response) {
      if (!response) return {}
      if (response.data && (response.data.headers || response.data.records || response.data.total || response.data.pages)) {
        return response.data
      }
      return response
    },
    unwrapMqtlResourceList(response) {
      if (Array.isArray(response)) return response
      if (response && Array.isArray(response.data)) return response.data
      return []
    },
    normalizeMqtlResource(resource) {
      if (!resource) return null

      const rawType = (resource.type || '').toString().trim().toLowerCase()
      const kind = rawType.startsWith('image') || rawType === 'image' ? 'image' : 'file'
      const src = this.buildMqtlResourceSrc(kind, rawType, resource.data)

      if (!src) return null

      return {
        name: resource.name || '',
        kind,
        src
      }
    },
    buildMqtlResourceSrc(kind, type, data) {
      if (!data || typeof data !== 'string') return ''

      const payload = data.trim()
      if (!payload) return ''
      if (payload.startsWith('data:')) return payload
      if (/^(https?:)?\/\//.test(payload) || payload.startsWith('/')) return payload

      if (kind === 'image') {
        const imageType = type && type !== 'image' ? type : 'image/png'
        return `data:${imageType};base64,${payload.replace(/\s+/g, '')}`
      }

      const fileType = type && type !== 'file' ? type : 'application/octet-stream'
      return `data:${fileType};base64,${payload.replace(/\s+/g, '')}`
    },
    handleMqtlResourceError(index, kind) {
      const targetList = kind === 'image' ? this.imageResources : this.fileResources
      const target = targetList[index]

      if (!target) return

      this.mqtlResources = this.mqtlResources.filter(item => item !== target)
    },
    handleImageError() {
      console.error('Failed to load image')
      this.topTraitImages = []
    }
  }
}
</script>

<style lang="less" scoped>
.gene {
  min-height: 100%;
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, #fef2f2 0%, #ffffff 100%);
}

.bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.molecule-bg {
  position: absolute;
  width: 400px;
  height: 400px;
  top: 20px;
  left: -50px;
}

.dna-bg {
  position: absolute;
  width: 200px;
  height: 500px;
  top: -50px;
  right: 0;
}

.bars-bg {
  position: absolute;
  width: 200px;
  height: 300px;
  bottom: 80px;
  left: -20px;
}

.gene-content {
  position: relative;
  z-index: 1;
  margin: 0 auto;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px);
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  justify-content: center;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: #991b1b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
  transition: opacity 0.2s;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    opacity: 0.7;
  }
}

.breadcrumb-sep {
  color: #d1d5db;
  font-size: 13px;
}

.breadcrumb-current {
  font-size: 13px;
  font-weight: 600;
  color: #991b1b;
}

.header-section {
  text-align: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 40px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.page-subtitle {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
  line-height: 1.6;
}

.page-description {
  max-width: 980px;
  font-size: 14px;
  color: #4b5563;
  margin: 12px auto 0;
  line-height: 1.7;
  text-align: center;
}

.filters-section {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  justify-content: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.filter-select {
  min-width: 220px;
}

.browse-btn {
  margin-left: 8px;
}

.filter-divider {
  width: 1px;
  height: 32px;
  background: #e5e7eb;
  align-self: flex-end;
  margin: 0 4px;
}

.main-content {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 16px;
  flex: 1;
  align-items: stretch;
}

.results-panel {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
}

.chart-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.panel-header {
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;

  svg {
    width: 18px;
    height: 18px;
    color: #ef4444;
  }
}

.panel-subtitle {
  font-size: 12px;
  color: #9ca3af;
  flex: 1;
}

.download-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%);
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: 10px;
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.12);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: linear-gradient(135deg, #fee2e2 0%, #fecdd3 100%);
    border-color: #fca5a5;
    color: #7f1d1d;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

.table-container {
  flex: none;
  margin-top: 12px;
  max-height: 520px;
  overflow-y: auto;
  overflow-x: auto;
}

.results-glossary {
  margin: 12px 16px 16px;
}

.trait-link {
  color: #991b1b;
  text-decoration: underline;
  text-decoration-color: rgba(153, 27, 27, 0.35);
  text-underline-offset: 2px;
  cursor: pointer;
}

.trait-link:hover {
  color: #b91c1c;
}

.results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: auto;

  th {
    padding: 10px 12px;
    text-align: left;
    font-weight: 600;
    color: #374151;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  td {
    padding: 10px 12px;
    color: #4b5563;
    border-bottom: 1px solid #f3f4f6;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }

  tbody tr {
    transition: background 0.15s ease;

    &:hover {
      background: #fafafa;
    }
  }
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.header-label {
  display: inline-block;
  vertical-align: middle;
}

.sort-icons {
  display: inline-flex;
  gap: 2px;
  margin-left: 6px;
  vertical-align: middle;
}

.sort-icon {
  color: #c4c4c4;
  font-size: 12px;
  line-height: 1;
  transition: color 0.2s ease;
}

.sort-icon.active,
.sortable-header.active .sort-icon.active {
  color: #dc2626;
}

.table-footer {
  padding: 10px 16px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-info {
  font-size: 13px;
  color: #9ca3af;
}

.pagination {
  display: flex;
  align-items: center;

  :deep(.el-pagination) {
    padding: 0;
    font-weight: 400;
  }

  :deep(.el-pagination.is-background .btn-prev),
  :deep(.el-pagination.is-background .btn-next),
  :deep(.el-pagination.is-background .el-pager li) {
    min-width: 32px;
    height: 32px;
    line-height: 32px;
    margin: 0 2px;
    border-radius: 4px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #6b7280;
    transition: all 0.2s ease;
  }

  :deep(.btn-prev),
  :deep(.btn-next),
  :deep(.el-pager li) {
    min-width: 32px;
    height: 32px;
    line-height: 32px;
    margin: 0 2px;
    border-radius: 4px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #6b7280;
    transition: all 0.2s ease;
  }

  :deep(.btn-prev),
  :deep(.btn-next) {
    padding: 0;
  }

  :deep(.el-pagination.is-background .btn-prev:hover),
  :deep(.el-pagination.is-background .btn-next:hover),
  :deep(.el-pagination.is-background .el-pager li:hover) {
    border-color: #ef4444;
    color: #ef4444 !important;
    background: #ffffff;
  }

  :deep(.el-pagination.is-background .el-pager li:not(.disabled).active) {
    background: #991b1b;
    border-color: #991b1b;
    color: #ffffff;
  }

  :deep(.btn-prev:disabled),
  :deep(.btn-next:disabled) {
    background: #ffffff;
    border-color: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
  }

  :deep(.el-pager li.btn-quicknext),
  :deep(.el-pager li.btn-quickprev) {
    color: #6b7280;
  }

  :deep(.el-pager li.btn-quicknext:hover),
  :deep(.el-pager li.btn-quickprev:hover) {
    color: #ef4444 !important;
  }
}

.charts-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 100%;
}

.mqtl-module {
  padding: 16px;
}

.mqtl-section {
  margin-top: 16px;
}

.mqtl-filters {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.mqtl-content {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 16px;
  align-items: stretch;
}

.mqtl-results-panel {
  min-width: 0;
}

.mqtl-resource-panel {
  min-width: 0;
}

.empty-cell {
  text-align: center;
  color: #9ca3af;
  padding: 40px 12px;
  font-size: 14px;
}

.panel-title-sm {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  padding: 12px 16px 8px;

  svg {
    width: 18px;
    height: 18px;
    color: #ef4444;
  }
}

.chart-container {
  padding: 12px 16px 20px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 360px;
}

.images-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;
  height: 100%;
  padding: 8px 0;
  align-items: center;
}

.top-trait-image {
  width: 100%;
  max-width: none;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
}

:deep(.top-trait-image .el-image__inner) {
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
}

.image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 20px;
  color: #9ca3af;
  font-size: 13px;
  text-align: center;

  svg {
    width: 48px;
    height: 48px;
    color: #d1d5db;
  }
}

.resource-container {
  padding: 16px;
  flex: 1;
}

.resource-section + .resource-section {
  margin-top: 20px;
}

.resource-section-title {
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.resource-file-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.resource-file-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  text-decoration: none;
  transition: all 0.2s ease;

  svg {
    width: 18px;
    height: 18px;
    color: #ef4444;
    flex-shrink: 0;
  }

  &:hover {
    border-color: #ef4444;
    color: #ef4444;
    background: #fff7f7;
  }
}

.resource-file-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chart-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px 16px;
  font-size: 13px;
  color: #6b7280;

  svg {
    width: 16px;
    height: 16px;
    color: #ef4444;
    margin-right: 8px;
    flex-shrink: 0;
  }
}

@media (max-width: 1200px) {
  .main-content {
    grid-template-columns: 1fr;
  }

  .mqtl-content {
    grid-template-columns: 1fr;
  }

  .top-trait-image {
    max-width: 400px;
  }
}

@media (max-width: 768px) {
  .gene-content {
    padding: 24px 16px;
  }

  .page-title {
    font-size: 28px;
  }

  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-select {
    min-width: 100%;
  }

  .browse-btn {
    justify-content: center;
  }

  .results-table {
    font-size: 11px;

    th, td {
      padding: 8px 10px;
    }
  }
}
</style>
